import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import type { DcaWorkspaceWellApi } from 'types/dca-workspace-wells-api/responses';
import type { DcaWorkspaceWellSelectedJob } from 'types/dca-workspace-wells-api/selected-job';
import styles from './dca-workspace-content.module.scss';

export type DcaWorkspaceWellTableRow = DcaWorkspaceWellApi & {
  selectedJob: DcaWorkspaceWellSelectedJob | null;
};

const columnHelper = createColumnHelper<DcaWorkspaceWellTableRow>();

/**
 * If a nested job is selected, use its value. Otherwise, use the `globalSaved` well field.
 * @template T
 * @param {DcaWorkspaceWellTableRow} row well row from parent table
 * @param {function} fromSelected function that gets the column value from the selected job
 * @param {function} fromFallback function that gets the column value from the well's `globalSaved` field
 * @return {T | null | undefined} Coalesced value
 */
function fromSelectedJobOrElse<T>(
  row: DcaWorkspaceWellTableRow,
  fromSelected: (
    job: NonNullable<DcaWorkspaceWellTableRow['selectedJob']>
  ) => T,
  fromFallback: (row: DcaWorkspaceWellTableRow) => T
): T {
  return row.selectedJob ? fromSelected(row.selectedJob) : fromFallback(row);
}

export const wellInformationColumns: ColumnDef<
  DcaWorkspaceWellTableRow,
  any
>[] = [
  columnHelper.accessor('uwi', {
    id: 'uwi',
    header: 'UWI',
    size: 140,
    meta: { type: 'text' },
    cell: (info) => {
      const uwi = info.getValue();
      if (!uwi) return '—';
      return (
        <a
          href={`/wells/${uwi}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.uwiLink}
          onClick={(e) => e.stopPropagation()}
        >
          {uwi}
        </a>
      );
    },
  }),
  columnHelper.accessor('wellName', {
    id: 'wellName',
    header: 'Well Name',
    size: 200,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('holeDirection', {
    id: 'holeDirection',
    header: 'Hole Direction',
    size: 130,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('currentOperator', {
    id: 'currentOperator',
    header: 'Current Operator',
    size: 180,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('spudDate', {
    id: 'spudDate',
    header: 'Spud Date',
    size: 220,
    meta: { type: 'date' },
  }),
  columnHelper.accessor('completionDate', {
    id: 'completionDate',
    header: 'Completion Date',
    size: 220,
    meta: { type: 'date' },
  }),
  columnHelper.accessor('firstProductionDate', {
    id: 'firstProductionDate',
    header: 'First Production Date',
    size: 220,
    meta: { type: 'date' },
  }),
  columnHelper.accessor('totalMonthsOnline', {
    id: 'totalMonthsOnline',
    header: 'Total Months Online',
    size: 200,
    meta: { align: 'left', type: 'integer' },
  }),
  columnHelper.accessor('trueVerticalDepthFt', {
    id: 'trueVerticalDepthFt',
    header: 'True Vertical Depth (ft)',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
  }),
  columnHelper.accessor('perforatedLateralLengthFt', {
    id: 'perforatedLateralLengthFt',
    header: 'Perforated Lateral Length (ft)',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
  }),
  columnHelper.accessor('srpLandingZone', {
    id: 'srpLandingZone',
    header: 'SRP Landing Zone',
    size: 160,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('typeCurveFormation', {
    id: 'typeCurveFormation',
    header: 'Type Curve Formation',
    size: 170,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('typeCurveArea', {
    id: 'typeCurveArea',
    header: 'Type Curve Area',
    size: 150,
    meta: { type: 'text' },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.typeCurveDisplay || null,
        (r) => r.typeCurve
      ),
    {
      id: 'typeCurve',
      header: 'Type Curve',
      size: 140,
      meta: { type: 'text' },
    }
  ),
  columnHelper.accessor('earlyTimeWell', {
    id: 'earlyTimeWell',
    header: 'Early Time Well',
    size: 130,
    meta: { type: 'boolean' },
    cell: (info) => (info.getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.oilEurBbl,
        (r) => r.globalSaved?.oilEurBbl ?? null
      ),
    {
      id: 'oilEurBbl',
      header: 'Oil EUR (bbl)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.oilEurPercentile ?? null, {
    id: 'oilEurPercentile',
    header: 'Oil EUR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.oilEurPerFtBblFt,
        (r) => r.globalSaved?.oilEurPerFtBblFt ?? null
      ),
    {
      id: 'oilEurPerFtBblFt',
      header: 'Oil EUR/ft (bbl/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row.globalSaved?.oilEurPerFtPercentile ?? null,
    {
      id: 'oilEurPerFtPercentile',
      header: 'Oil EUR/ft Percentile',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
    }
  ),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.gasEurMcf,
        (r) => r.globalSaved?.gasEurMcf ?? null
      ),
    {
      id: 'gasEurMcf',
      header: 'Gas EUR (mcf)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.gasEurPercentile ?? null, {
    id: 'gasEurPercentile',
    header: 'Gas EUR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.gasEurPerFtMcfFt,
        (r) => r.globalSaved?.gasEurPerFtMcfFt ?? null
      ),
    {
      id: 'gasEurPerFtMcfFt',
      header: 'Gas EUR/ft (mcf/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row.globalSaved?.gasEurPerFtPercentile ?? null,
    {
      id: 'gasEurPerFtPercentile',
      header: 'Gas EUR/ft Percentile',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
    }
  ),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.waterEurBbl,
        (r) => r.globalSaved?.waterEurBbl ?? null
      ),
    {
      id: 'waterEurBbl',
      header: 'Water EUR (bbl)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.waterEurPercentile ?? null, {
    id: 'waterEurPercentile',
    header: 'Water EUR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.waterEurPerFtBblFt,
        (r) => r.globalSaved?.waterEurPerFtBblFt ?? null
      ),
    {
      id: 'waterEurPerFtBblFt',
      header: 'Water EUR/ft (bbl/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row.globalSaved?.waterEurPerFtPercentile ?? null,
    {
      id: 'waterEurPerFtPercentile',
      header: 'Water EUR/ft Percentile',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
    }
  ),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.gorMcfBbl,
        (r) => r.globalSaved?.gorMcfBbl ?? null
      ),
    {
      id: 'gorMcfBbl',
      header: 'GOR (mcf/bbl)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.gorPercentile ?? null, {
    id: 'gorPercentile',
    header: 'GOR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.cgrBblMmcf,
        (r) => r.globalSaved?.cgrBblMmcf ?? null
      ),
    {
      id: 'cgrBblMmcf',
      header: 'CGR (bbl/Mmcf)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.cgrPercentile ?? null, {
    id: 'cgrPercentile',
    header: 'CGR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.worBblBbl,
        (r) => r.globalSaved?.worBblBbl ?? null
      ),
    {
      id: 'worBblBbl',
      header: 'WOR (bbl/bbl)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.worPercentile ?? null, {
    id: 'worPercentile',
    header: 'WOR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.wgrBblMmcf,
        (r) => r.globalSaved?.wgrBblMmcf ?? null
      ),
    {
      id: 'wgrBblMmcf',
      header: 'WGR (bbl/Mmcf)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor((row) => row.globalSaved?.wgrPercentile ?? null, {
    id: 'wgrPercentile',
    header: 'WGR Percentile',
    size: 200,
    meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 1 } },
  }),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.jobIdDisplay,
        (r) => r.globalSaved?.jobId ?? null
      ),
    {
      id: 'jobIdDisplay',
      header: 'Job ID',
      size: 100,
      meta: { type: 'text' },
      cell: (info) => {
        const v = info.getValue() as number | string | null | undefined;
        if (v === null || v === undefined || v === '') return '—';
        return String(v);
      },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.jobDate ?? null,
    {
      id: 'jobDate',
      header: 'Job Date',
      size: 220,
      meta: { type: 'date' },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.proppantLbsPerFt ?? null,
    {
      id: 'proppantLbsPerFt',
      header: 'Proppant Intensity (lbs/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.fluidGalPerFt ?? null,
    {
      id: 'fluidGalPerFt',
      header: 'Fluid Intensity (gal/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.fluidBblPerFt ?? null,
    {
      id: 'fluidBblPerFt',
      header: 'Fluid Intensity (bbl/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.waterGalPerFt ?? null,
    {
      id: 'waterGalPerFt',
      header: 'Water Intensity (gal/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.waterBblPerFt ?? null,
    {
      id: 'waterBblPerFt',
      header: 'Water Intensity (bbl/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.acidGalPerFt ?? null,
    {
      id: 'acidGalPerFt',
      header: 'Acid Intensity (gal/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.acidBblPerFt ?? null,
    {
      id: 'acidBblPerFt',
      header: 'Acid Intensity (bbl/ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 2 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.stageSpacingFt ?? null,
    {
      id: 'stageSpacingFt',
      header: 'Stage Spacing (ft)',
      size: 200,
      meta: { align: 'left', type: 'decimal', format: { maxDecimalPlaces: 0 } },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.proppantMeshSize ?? null,
    {
      id: 'proppantMeshSize',
      header: 'Proppant Mesh Size',
      size: 160,
      meta: { type: 'text' },
    }
  ),
  columnHelper.accessor(
    (row) => row?.latestWellStimulationDetails?.fluidSystem ?? null,
    {
      id: 'fluidSystem',
      header: 'Fluid System',
      size: 150,
      meta: { type: 'text' },
    }
  ),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.createdAt,
        (r) => r.globalSaved?.createdAt ?? null
      ),
    {
      id: 'createdAtDisplay',
      header: 'Created At',
      size: 220,
      meta: { type: 'date' },
    }
  ),
  columnHelper.accessor(
    (row) =>
      fromSelectedJobOrElse(
        row,
        (j) => j.createdBy,
        (r) => r.globalSaved?.createdBy ?? null
      ),
    {
      id: 'createdByDisplay',
      header: 'Created By',
      size: 140,
      meta: { type: 'text' },
    }
  ),
];
